import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { Stack } from '@mui/material';
import { useConfirmingRequests } from 'lib/api/confirming';
import {
  formatDateString,
  formatMoney,
  getShortString,
} from 'lib/functions/utils';
import {
  BottomDock,
  BoxItemStack,
  ContentCard,
  FilterDock,
  FilterGroup,
  type FilterPillOption,
  FilterPills,
  getConfirmingStatusChipType,
  getConfirmingStatusLabel,
  PaginationDock,
  type StatusChipType,
} from 'lib/mobileDesignSystem';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { buildPaginatedQuery } from '../../lib/queryUtils';

const PAGE_SIZE = 10;
const DEFAULT_STATUS_VALUES = ['IN_REVIEW', 'ACCEPTED', 'IN_TRANSFER'];

const STATUS_OPTIONS: FilterPillOption[] = [
  { label: 'Creada', value: 'CREATED' },
  { label: 'En revisión', value: 'IN_REVIEW' },
  { label: 'Aceptada', value: 'ACCEPTED' },
  { label: 'Rechazada', value: 'REJECTED' },
  { label: 'En giro', value: 'IN_TRANSFER' },
  { label: 'Activa', value: 'ACTIVE' },
  { label: 'Completada', value: 'COMPLETED' },
];

const ConfirmingRequests: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('c_page') ?? '1');
  const appliedStatuses =
    searchParams.get('c_statuses')?.split(',').filter(Boolean) ??
    DEFAULT_STATUS_VALUES;

  const [searchStatuses, setSearchStatuses] = useState<FilterPillOption[]>(() =>
    appliedStatuses.map(
      (v) =>
        STATUS_OPTIONS.find((o) => o.value === v) ?? { label: v, value: v },
    ),
  );

  const setPage = (newPage: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('c_page', String(newPage));
        return next;
      },
      { replace: true },
    );
  };

  const {
    data: requests = {
      data: [],
      pagination: { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 },
    },
    isLoading,
    isFetching,
  } = useConfirmingRequests(
    dispatch,
    buildPaginatedQuery({
      page,
      pageSize: PAGE_SIZE,
      filters: { statuses: appliedStatuses },
    }),
  );

  const handleSearch = useCallback(() => {
    const newStatuses = searchStatuses.map((s) => s.value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('c_statuses', newStatuses.join(','));
        next.set('c_page', '1');
        return next;
      },
      { replace: true },
    );
  }, [searchStatuses, setSearchParams]);

  const items = requests.data.map((item) => ({
    primaryText: getShortString(item.stakeholderName, 20),
    amount: formatMoney(item.amount),
    secondaryText:
      formatDateString(item.createdAt) + ' · ' + item.userOwner?.name,
    chips: [
      {
        text: item.id.toString(),
        type: 'info' as StatusChipType,
      },
      {
        text: getConfirmingStatusLabel(item.status),
        type: getConfirmingStatusChipType(item.status),
      },
    ],
    onClick: () => navigate(`/finance/confirming/${item.id}`),
  }));

  return (
    <Stack spacing={2} sx={{ pb: 10 }}>
      <ContentCard
        header={{
          icon: <ManageSearchIcon sx={{ fontSize: 20 }} />,
          title: 'Todas las solicitudes',
          description: 'Gestiona tus solicitudes de confirming.',
        }}
      >
        <BoxItemStack items={items} loading={isLoading} skeletonCount={10} />
      </ContentCard>
      <BottomDock
        center={
          <PaginationDock
            page={page}
            totalPages={requests.pagination?.totalPages}
            onPageChange={setPage}
            loading={isFetching}
          />
        }
        right={
          <FilterDock title="Filtros" onSearch={handleSearch}>
            <FilterGroup label="Estados">
              <FilterPills
                options={STATUS_OPTIONS}
                value={searchStatuses}
                onChange={setSearchStatuses}
              />
            </FilterGroup>
          </FilterDock>
        }
      />
    </Stack>
  );
};

export default ConfirmingRequests;
